// utils/api.js
import axios from 'axios'

// 确保使用正确的 baseURL
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
})

// 添加请求日志，方便调试
api.interceptors.request.use(config => {
  console.log('API Request:', {
    method: config.method,
    url: config.url,
    fullURL: config.baseURL + config.url
  })

  const isAdminPath = config.url?.startsWith('/admin')
  const token = isAdminPath
    ? localStorage.getItem('admin_token')
    : localStorage.getItem('uk_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

let isRedirecting = false

api.interceptors.response.use(
  res => {
    console.log('API Response:', res.config.url, res.status)
    return res.data
  },
  err => {
    console.error('API Error:', {
      url: err.config?.url,
      status: err.response?.status,
      message: err.response?.data?.message
    })

    const msg = err.response?.data?.message || err.response?.data?.error || '网络请求失败'
    const status = err.response?.status
    const url = err.config?.url || ''

    if (status === 401) {
      const isAdminRequest = url.startsWith('/admin')
      if (isAdminRequest) {
        localStorage.removeItem('admin_token')
        localStorage.removeItem('admin_info')
        if (!isRedirecting && !window.location.pathname.includes('/admin/login')) {
          isRedirecting = true
          import('@/router').then(({ default: router }) => {
            router.push('/admin/login').finally(() => { isRedirecting = false })
          })
        }
      } else {
        localStorage.removeItem('uk_token')
        localStorage.removeItem('uk_address')
        localStorage.removeItem('uk_invite_code')
        localStorage.removeItem('uk_registered')
        window.dispatchEvent(new CustomEvent('uk-token-expired'))
      }
    }
    return Promise.reject(new Error(msg))
  }
)


// ===== 认证 =====
export const authAPI = {
  getNonce: (address) => api.get('/auth/nonce', { params: { address } }),
  login: (data) => api.post('/auth/login', data),
  adminLogin:      (data)     => api.post('/admin/login', data),           // 保留兼容
  adminGoogleLogin:(data)     => api.post('/admin/google-login', data),    // 新增
  checkRegistered: (address) => api.get(`/auth/check/${address}`),
}

// ===== 公开接口 =====
export const publicAPI = {
  getPrice: () => api.get('/public/price'),
  getPriceTrend: (range) => api.get('/public/price/trend', { params: { range } }),
  getStatistics: () => api.get('/public/statistics'),
  getNodeStats: () => api.get('/public/node/stats'),
  getBurnStats: () => api.get('/public/burn/stats'),
  getContracts: () => api.get('/public/contracts'),
  getTopNodes: (limit) => api.get('/public/top/nodes', { params: { limit } }),
  getAnnouncement: () => api.get('/public/announcement'),
  getServerTime: () => api.get('/public/time'),
  getServerTime1: () => api.get('/public/time1'),
}

// ===== Swap =====
export const swapAPI = {
  getTokenInfo: () => api.get('/swap/token/info'),
  getStats: () => api.get('/swap/stats'),
  getPriceHistory: (params) => api.get('/swap/price/history', { params }),
  // 兼容旧调用
  getInfo: () => api.get('/public/price'),
}

// ===== 用户 =====
export const userAPI = {
  getProfile: () => api.get('/user/info'),
  getStatistics: () => api.get('/user/statistics'),
  getBalance: () => api.get('/user/info'), // 余额在 /user/info 里
  getTeam: (params) => api.get('/user/team', { params }),
  queryTeam: (address) => api.get('/team/query', { params: { address } }),
  updateProfile: (data) => api.put('/user/profile', data),
  getTransactions: (params) => api.get('/user/statistics', { params }), // 虚拟账本
  getDailyRewards: (params) => api.get('/reward/summary', { params }),
  getWithdrawableAmounts: () => api.get('/user/withdrawable-amounts'),
  // 提现
  requestWithdraw: (data) => api.post('/withdraw/apply', data),
  getWithdrawList: (params) => api.get('/withdraw/list', { params }),
  getWithdrawSignature: (requestId) => api.get('/withdraw/signature', { params: { request_id: requestId } }),
  confirmWithdraw: (data) => api.post('/withdraw/confirm', data),
  getPendingWithdraw: () => api.get('/withdraw/pending'),
}

// ===== 节点 =====
export const nodeAPI = {
  purchase: (data) => api.post('/node/purchase', data),
  getInfo: () => api.get('/node/info'),
  getMyNode: () => api.get('/node/info'),
  getNodeList: (params) => api.get('/node/list', { params }),
  getReleases: (params) => api.get('/node/releases', { params }),
  getStatistics: () => api.get('/node/statistics'),
  // 购买节点：前端先发起链上TX，再提交txhash给后端
  purchase: (data) => api.post('/node/info', data), // 后端记录节点信息
  // 领取节点释放
  claimRelease: () => api.post('/withdraw/apply', { withdraw_type: 1 }),
}

// ===== 预测 =====
export const predictionAPI = {
  join: (data) => api.post('/prediction/join', data),
  getNetworkStats: () => api.get('/prediction/network-stats'),
  getMyPredictions: () => api.get('/prediction/my'),
  create: (data) => api.post('/prediction/create', data),
  getList: (params) => api.get('/prediction/list', { params }),
  getDetail: (id) => api.get(`/prediction/${id}`),
  getStatistics: () => api.get('/prediction/statistics'),
  getNetworkStats: () => api.get('/prediction/global'),
  getWeight: () => api.get('/prediction/weight'),
  getRanking: (params) => api.get('/prediction/ranking', { params }),
}

// ===== 质押 =====
export const stakeAPI = {
  getList: (params) => api.get('/stake/list', { params }),
}

// ===== 奖励 =====
export const rewardAPI = {
  getLiquidityRewards: (params) => api.get('/reward/liquidity', { params }),
  getCommunityRewards: (params) => api.get('/reward/community', { params }),
  getNodeDividend: (params) => api.get('/reward/dividend', { params }),
  getSummary: () => api.get('/reward/summary'),
  getMonthly: (params) => api.get('/reward/monthly', { params }),
}

// ===== 社区 =====
export const communityAPI = {
  getInfo: () => api.get('/community/info'),
  getTeam: (params) => api.get('/community/team', { params }),
  applySubsidy: (data) => api.post('/community/subsidy/apply', data),
  getSubsidyHistory: () => api.get('/community/subsidy/history'),
  getProgress: () => api.get('/community/progress'),
  getLevelReqs: () => api.get('/community/level/requirements'),
  getRank: (params) => api.get('/community/rank', { params }),
}

// ===== 团队 =====
export const teamAPI = {
  getDirectLines: () => api.get('/team/direct'),
  getPerformance: () => api.get('/team/performance'),
  queryByAddress: (address) => api.get('/team/query', { params: { address } }),
  getLevelInfo: () => api.get('/team/level'),
}

// ===== 管理员 =====
export const adminAPI = {
  // 仪表盘
  getDashboard: () => api.get('/admin/dashboard'),
  changePassword: (data) => api.post('/admin/change-password', data),
  resetAdminPassword: (id, data) => api.post(`/admin/admins/${id}/reset-password`, data),

  // 获取买入开关状态
  getCanBuyStatus: () => api.get('/admin/can-buy-status'),

  // 设置买入开关
  setCanBuyStatus: (canBuy) => api.post('/admin/can-buy-status', { can_buy: canBuy }),

  bindGoogleEmail: (id, data) => api.put(`/admin/admins/${id}/google-email`, data),

  // 获取高倍质押占比配置
  getHighStakeRatioConfig: () => api.get('/admin/high-stake-ratio-config'),

  // 更新高倍质押占比配置
  updateHighStakeRatioConfig: (percentage) =>
    api.put('/admin/high-stake-ratio-config', { percentage }),

  // 用户
  getUsers: (params) => api.get('/admin/users', { params }),
  getUserDetail: (id) => api.get(`/admin/users/${id}`),
  getUserTeamTree: (id, params) => api.get(`/admin/users/${id}/team-tree`, { params }),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  getRewardDetails: (params) => api.get('/admin/reward-details', { params }),
  // 节点
  getNodes: (params) => api.get('/admin/nodes', { params }),
  getNodeDetail: (id) => api.get(`/admin/nodes/${id}`),
  releaseNode: (data) => api.post('/admin/nodes/release', data),
  // 预测
  getPredictions: (params) => api.get('/admin/predictions', { params }),
  // 提现审核
  getWithdraws: (params) => api.get('/admin/withdraws', { params }),
  approveWithdraw: (id, data) => api.post(`/admin/withdraws/${id}/approve`, data),
  rejectWithdraw: (id, data) => api.post(`/admin/withdraws/${id}/reject`, data),
  // 社区补贴
  getSubsidies: (params) => api.get('/admin/subsidies', { params }),
  auditSubsidy: (id, data) => api.post(`/admin/subsidies/${id}/audit`, data),
  paySubsidy: (id) => api.post(`/admin/subsidies/${id}/pay`),
  batchPaySubsidies: () => api.post('/admin/subsidies/batch-pay'),
  // 燃烧
  manualBurn: (data) => api.post('/admin/burn/manual', data),
  getBurnRecords: (params) => api.get('/admin/burn/records', { params }),
  // 配置
  getConfig: () => api.get('/admin/config'),
  updateConfig: (data) => api.put('/admin/config', data),
  // 触发任务
  triggerNodeRelease: () => api.post('/admin/trigger/node-release'),
  triggerForceSell: () => api.post('/admin/trigger/force-sell'),
  triggerFuseBurn: () => api.post('/admin/trigger/fuse-burn'),
  triggerDistribute: () => api.post('/admin/trigger/distribute'), // 兼容旧调用
  // 链上
  getChainWallets: () => api.get('/admin/chain/wallets'),
  getPoolPending: () => api.get('/admin/chain/pool-pending'),
  getPoolPendingByMultiply: () => api.get('/admin/chain/pool-pending/by-multiply'),
  addToPool: (data) => api.post('/admin/chain/add-to-pool', data),
  // 白名单批量质押
  batchStake: (data) => api.post('/admin/whitelist/batch-stake', data),
  // 管理员账号
  getAdmins: (params) => api.get('/admin/admins', { params }),
  createAdmin: (data) => api.post('/admin/admins', data),
  deleteAdmin: (id) => api.delete(`/admin/admins/${id}`),
  // 系统
  getSystemHealth: () => api.get('/admin/system/health'),
  getLogs: (params) => api.get('/admin/system/logs', { params }),
  exportData: (params) => api.get('/admin/export', { params, responseType: 'blob' }),

  // 获取系统设置
  getSystemSettings: () => api.get('/admin/settings'),

  // 更新系统设置
  updateSystemSettings: (settings) => api.put('/admin/settings', settings),


  // 管理员管理
  getAdminList: (params) => api.get('/admin/admins', { params }),
  createAdmin: (data) => api.post('/admin/admins', data),
  updateAdminRole: (id, data) => api.put(`/admin/admins/${id}/role`, data),
  deleteAdmin: (id) => api.delete(`/admin/admins/${id}`),

  // 添加白名单钱包
  addWhitelistWallet: (data) => api.post('/admin/whitelist/wallets', data),

  // 获取白名单钱包列表
  getWhitelistWallets: () => api.get('/admin/whitelist/wallets'),

  // 删除白名单钱包
  deleteWhitelistWallet: (address) => api.delete(`/admin/whitelist/wallets/${address}`),


  getPendingBurn: () => api.get('/admin/burn/pending'),
  submitBurn: (data) => api.post('/admin/burn/submit', data),
  getBurnHistory: (params) => api.get('/admin/burn/history', { params }),

  // // 兼容旧调用
  // getStats:        ()       => api.get('/admin/dashboard'),
  // reviewWithdraw:  (id, data) => {
  //   if (data.action === 'approve') return api.post(`/admin/withdraws/${id}/approve`, { remark: data.remark || '审核通过' })
  //   return api.post(`/admin/withdraws/${id}/reject`, { remark: data.reject_reason || data.remark || '拒绝' })
  // },
  // poolOperation:   (data)   => api.post('/admin/chain/add-to-pool', data),
  // getPoolOps:      (params) => api.get('/admin/chain/pool-pending', { params }),
  // updatePrice:     (price)  => api.put('/admin/config', { key: 'current_price', value: String(price) }),
}
